import { Subscription } from 'rxjs';
import {
    ApolloLink,
    FetchResult,
    NextLink,
    Observable,
    Observer,
    Operation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { bachPinboardSessionKeyQuery } from '../bach/bach.util';
import { getSessionFromCache } from '../stateful/stateful-service-hooks.util';

interface QueuedOperation {
    operation: Operation;
    forward: NextLink;
    observer: Observer<FetchResult>;
    /**
     * Set exactly once when the entry reaches a terminal state (result,
     * error, abort, or consumer unsubscribe). All cleanup paths funnel
     * through finishOperation(), which uses this flag to stay idempotent —
     * a completed operation must release exactly ONE queue slot, no matter
     * how many cleanup paths observe the completion.
     */
    finished: boolean;
    subscription?: Subscription;
    removeAbortListener?: () => void;
}

interface SerializationContext {
    serializeMutation?: {
        sessionId: string;
        mutationId?: string;
    };
    // Top-level, matching Apollo's standard context shape — this is where
    // useCancelableMutation (and HttpLink) read/write the abort signal.
    fetchOptions?: { signal?: AbortSignal };
    [key: string]: any;
}

const createAbortError = (): Error => {
    const error = new Error('The operation was aborted.');
    error.name = 'AbortError';
    return error;
};

/**
 * Serializes bach pinboard-session mutations per session: callosum guards
 * each session with a lock and rejects concurrent mutations with
 * 409 / code 14009 ("Resource was modified by another request"), so at most
 * one mutation may be in flight per session at any time.
 *
 * Invariants (encoded in pinboard-serializing-link.invariants.spec.ts):
 * 1. Strictly serial dispatch — a completion releases exactly one queued
 *    operation.
 * 2. Every operation terminates observably — an aborted operation settles
 *    with an AbortError (never a result-less complete, which would leave
 *    Apollo's mutate() promise permanently unsettled).
 */
export class PinboardSerializingLink extends ApolloLink {
    private operationQueues: Map<string, QueuedOperation[]> = new Map();

    private activeOperations: Map<string, QueuedOperation> = new Map();

    public request(
        operation: Operation,
        forward: NextLink,
    ): Observable<FetchResult> {
        const context = operation.getContext() as SerializationContext;
        const serializationInfo = context.serializeMutation;

        const operationType = (operation.query.definitions.find(
            def => def.kind === 'OperationDefinition',
        ) as any)?.operation;

        if (
            operationType !== 'mutation' ||
            !serializationInfo?.sessionId ||
            context?.fetchOptions?.signal?.aborted
        ) {
            return forward(operation);
        }

        const sessionId = serializationInfo.sessionId;
        const signal: AbortSignal | undefined = context?.fetchOptions?.signal;

        return new Observable<FetchResult>(
            (observer: Observer<FetchResult>) => {
                const entry: QueuedOperation = {
                    operation,
                    forward,
                    observer,
                    finished: false,
                };

                // Listen from ENQUEUE time (not dispatch time) so that an
                // operation aborted while still waiting in the queue settles
                // immediately and never reaches the network.
                if (signal?.addEventListener) {
                    const onAbort = () => this.abortOperation(sessionId, entry);
                    signal.addEventListener('abort', onAbort, { once: true });
                    entry.removeAbortListener = () =>
                        signal.removeEventListener('abort', onAbort);
                }

                const queue = this.operationQueues.get(sessionId) || [];
                queue.push(entry);
                this.operationQueues.set(sessionId, queue);

                this.tryProcessQueue(sessionId);

                return () => {
                    // Teardown runs both after terminal notifications and on
                    // consumer unsubscribe. Only the unsubscribe-before-
                    // termination case needs cleanup here; finishOperation()
                    // keeps this idempotent for the others.
                    if (this.finishOperation(sessionId, entry)) {
                        entry.subscription?.unsubscribe();
                        this.tryProcessQueue(sessionId);
                    }
                };
            },
        );
    }

    /**
     * Transitions an entry to its terminal state: detaches its abort
     * listener, removes it from the queue BY REFERENCE (never by position —
     * blind head-removal is what allowed one completion to evict an
     * unrelated in-flight operation), and frees the session slot if this
     * entry held it. Returns false if the entry already finished, making
     * every cleanup path safe to run more than once.
     */
    private finishOperation(
        sessionId: string,
        entry: QueuedOperation,
    ): boolean {
        if (entry.finished) {
            return false;
        }
        // Marking the shared queue entry is the point of this method — every
        // cleanup path must see the same terminal flag on the same object.
        // eslint-disable-next-line no-param-reassign
        entry.finished = true;
        entry.removeAbortListener?.();

        const queue = this.operationQueues.get(sessionId);
        if (queue) {
            const index = queue.indexOf(entry);
            if (index !== -1) {
                queue.splice(index, 1);
            }
            if (queue.length === 0) {
                this.operationQueues.delete(sessionId);
            }
        }

        if (this.activeOperations.get(sessionId) === entry) {
            this.activeOperations.delete(sessionId);
        }
        return true;
    }

    private abortOperation(sessionId: string, entry: QueuedOperation): void {
        if (!this.finishOperation(sessionId, entry)) {
            return;
        }
        // Abort the in-flight network request, if any. Unsubscribing first
        // guarantees the downstream error/complete can no longer race with
        // the AbortError we deliver below.
        entry.subscription?.unsubscribe();
        // Settle the consumer. Completing WITHOUT a result here would leave
        // the caller's mutate() promise pending forever (Apollo only
        // resolves a mutation on next() and rejects on error()).
        entry.observer.error(createAbortError());
        this.tryProcessQueue(sessionId);
    }

    private tryProcessQueue(sessionId: string): void {
        if (this.activeOperations.has(sessionId)) return;

        const queue = this.operationQueues.get(sessionId);
        if (!queue || queue.length === 0) {
            this.operationQueues.delete(sessionId);
            return;
        }

        // Aborted/canceled entries are removed from the queue eagerly by
        // finishOperation, so the head is always a live entry.
        const entry = queue[0];
        const { operation, forward, observer } = entry;

        // update the variables with the updated session
        operation.variables = {
            ...operation.variables,
            session: {
                ...operation.variables?.session,
                ...getSessionFromCache(
                    operation.getContext().cache,
                    sessionId,
                    'BachPinboardSession',
                    bachPinboardSessionKeyQuery,
                    ['contextBookSessions'],
                ),
            },
        };

        this.activeOperations.set(sessionId, entry);

        entry.subscription = forward(operation).subscribe({
            next: (result: FetchResult) => {
                if (!entry.finished) {
                    observer.next(result);
                }
            },
            error: (error: any) => {
                if (this.finishOperation(sessionId, entry)) {
                    observer.error(error);
                }
                this.tryProcessQueue(sessionId);
            },
            complete: () => {
                if (this.finishOperation(sessionId, entry)) {
                    observer.complete();
                }
                this.tryProcessQueue(sessionId);
            },
        });
    }
}
